/**
 * Email Inbox guide — inbound email triggers and allowed sender filtering.
 *
 * Covers creating email triggers via the task API, configuring allowed_senders,
 * and how inbound emails become teammate runs.
 */

export const emailInboxContent = `
Give your teammate an email address so it can receive and process inbound emails automatically.

## How It Works

1. Create a teammate with instructions for handling emails
2. Generate an email inbox — the teammate gets a unique \`@m8tes.ai\` address
3. Emails to that address trigger a run with the email content as input
4. The teammate processes the email using its configured tools

## Set Up an Email Inbox

\`\`\`python
from m8tes import M8tes

client = M8tes()

# 1. Create the teammate
bot = client.teammates.create(
    name="Support Inbox",
    instructions="Classify incoming emails as bug, question, or feature request. Reply to questions directly.",
    tools=["slack"],
    allowed_senders=["@acme.com", "vip@partner.com"],
)

# 2. Generate the email inbox
task = client.tasks.create(teammate_id=bot.id, instructions="Process this incoming email")
trigger = client.tasks.triggers.create(task.id, type="email")
print(f"Email: {trigger.address}")  # support-inbox-x7k@m8tes.ai

\`\`\`

\`\`\`bash
# Create teammate
curl -X POST https://m8tes.ai/api/v2/teammates \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Support Inbox", "instructions": "Process incoming emails.", "tools": ["slack"], "allowed_senders": ["@acme.com"]}'

# Create task + email trigger
curl -X POST https://m8tes.ai/api/v2/tasks \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1, "instructions": "Process this incoming email"}'

curl -X POST https://m8tes.ai/api/v2/tasks/1/triggers \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "email"}'
\`\`\`

> The email trigger is task-level by API design. Think of the task as "the teammate's inbox" — create it once and emails flow through automatically.

## Allowed Senders

By default (no \`allowed_senders\`), only emails from the API key owner's registered email are accepted. Emails from unknown senders are silently dropped.

Set \`allowed_senders\` to control who can trigger the teammate:

\`\`\`python
# Domain pattern — any @acme.com address
bot = client.teammates.create(
    name="Support Bot",
    instructions="Handle support emails",
    allowed_senders=["@acme.com"]
)

# Specific addresses
bot = client.teammates.create(
    name="VIP Bot",
    instructions="Handle VIP requests",
    allowed_senders=["ceo@acme.com", "vip@partner.com"]
)

# Update allowed senders later
client.teammates.update(bot.id, allowed_senders=["@acme.com", "@partner.com"])
\`\`\`

## Multi-Tenant Email

Scope email inboxes per end-user with \`user_id\`:

\`\`\`python
bot = client.teammates.create(
    name="Inbox Bot",
    instructions="Process incoming emails for this customer",
    allowed_senders=["alice@customer.com"],
    user_id="alice_123"
)
\`\`\`

## Reply Threading

When the teammate uses email tools to reply, the response goes back to the original sender in the same email thread. The conversation feels natural — the sender emails the teammate and gets a reply.

## What's Next

- [Webhook Triggers](/docs/webhook-triggers) — trigger runs from external systems
- [Scheduling](/docs/scheduling) — automate tasks with cron and intervals
- [Memories](/docs/memories) — personalize teammate behavior per end-user
`;
